import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Key, Plus, Copy, Trash2, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { api } from '../lib/api'

export default function ApiKeysPage() {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyData, setNewKeyData] = useState<{ key: string } | null>(null)
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()

  const { data: apiKeys, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.getApiKeys(),
  })

  const createKeyMutation = useMutation({
    mutationFn: (name: string) => api.createApiKey({ name }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      setNewKeyData(data)
      setNewKeyName('')
      setShowCreateForm(false)
    }
  })

  const deleteKeyMutation = useMutation({
    mutationFn: (keyId: string) => api.deleteApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    }
  })

  const handleCreateKey = (e: React.FormEvent) => {
    e.preventDefault()
    if (newKeyName.trim()) {
      createKeyMutation.mutate(newKeyName.trim())
    }
  }

  const handleDeleteKey = async (keyId: string, keyName: string) => {
    if (window.confirm(`Are you sure you want to delete API key "${keyName}"? This action cannot be undone.`)) {
      deleteKeyMutation.mutate(keyId)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const toggleKeyVisibility = (keyId: string) => {
    const newVisible = new Set(visibleKeys)
    if (newVisible.has(keyId)) {
      newVisible.delete(keyId)
    } else {
      newVisible.add(keyId)
    }
    setVisibleKeys(newVisible)
  }

  const maskKey = (key: string) => {
    if (key.length <= 8) return '*'.repeat(key.length)
    return key.substring(0, 4) + '*'.repeat(key.length - 8) + key.substring(key.length - 4)
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-gray-600">
            Manage API keys for programmatic access to the platform
          </p>
        </div>
        <button 
          onClick={() => setShowCreateForm(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Create API Key</span>
        </button>
      </div>

      {/* New Key Alert */}
      {newKeyData && (
        <div className="mb-6 rounded-md bg-yellow-50 p-4 border border-yellow-200">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">API Key Created</h3>
              <div className="mt-2">
                <p className="text-sm text-yellow-700 mb-2">
                  Save this key securely - it will not be shown again.
                </p>
                <div className="flex items-center space-x-2 bg-white p-2 rounded border">
                  <code className="flex-1 font-mono text-sm">{newKeyData.key}</code>
                  <button
                    onClick={() => copyToClipboard(newKeyData.key)}
                    className="text-yellow-600 hover:text-yellow-700"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <button
                  onClick={() => setNewKeyData(null)}
                  className="text-sm text-yellow-800 underline"
                >
                  I've saved the key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="mb-6 bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New API Key</h3>
          <form onSubmit={handleCreateKey} className="flex space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Enter key name (e.g., 'My App Integration')"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <button
              type="submit"
              disabled={createKeyMutation.isPending}
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {createKeyMutation.isPending ? 'Creating...' : 'Create Key'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false)
                setNewKeyName('')
              }}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* API Keys List */}
      {apiKeys?.keys && apiKeys.keys.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {apiKeys.keys.map((key: any) => (
              <li key={key.id}>
                <div className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 rounded-md p-2">
                        <Key className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {key.name}
                        </h3>
                        <div className="flex items-center space-x-4 mt-1">
                          <div className="flex items-center space-x-2">
                            <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                              {visibleKeys.has(key.id) ? key.key_preview : maskKey(key.key_preview)}
                            </code>
                            <button
                              onClick={() => toggleKeyVisibility(key.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {visibleKeys.has(key.id) ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => copyToClipboard(key.key_preview)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                          <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                          {key.last_used_at ? (
                            <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                          ) : (
                            <span>Never used</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteKey(key.id, key.name)}
                      disabled={deleteKeyMutation.isPending}
                      className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No API keys</h3>
          <p className="text-gray-500 mb-6">
            Create your first API key to enable programmatic access to the platform.
          </p>
          <button 
            onClick={() => setShowCreateForm(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Create Your First API Key
          </button>
        </div>
      )}
    </div>
  )
}